<template>
  <div class="modal is-active">
    <div class="modal-background" @click="$emit('close')"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">CSVファイルインポート</p>
        <button class="delete" @click="$emit('close')"></button>
      </header>
      
      <section class="modal-card-body">
        <!-- ファイルアップロードエリア -->
        <div class="file-upload-area" 
             @drop="handleDrop"
             @dragover.prevent
             @dragenter.prevent>
          <input type="file" 
                 ref="fileInput" 
                 @change="handleFileSelect" 
                 accept=".csv"
                 style="display: none;">
          
          <div v-if="!selectedFile" class="upload-prompt">
            <span class="mdi mdi-cloud-upload mdi-48px"></span>
            <p>CSVファイルをドラッグ&ドロップ</p>
            <p>または</p>
            <button @click="$refs.fileInput.click()" class="button is-primary">
              ファイルを選択
            </button>
          </div>
          
          <div v-else class="selected-file">
            <span class="mdi mdi-file-document"></span>
            {{ selectedFile.name }}
            <button @click="clearFile" class="button is-small">
              <span class="mdi mdi-close"></span>
            </button>
          </div>
        </div>
        
        <!-- フォーマット説明 -->
        <div class="format-info">
          <h4>CSVファイルフォーマット</h4>
          <table class="table is-bordered is-narrow">
            <thead>
              <tr>
                <th>name</th>
                <th>email</th>
                <th>tel</th>
                <th>remarks</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>山田太郎</td>
                <td>yamada@example.com</td>
                <td>090-1234-5678</td>
                <td>備考</td>
              </tr>
            </tbody>
          </table>
          <p class="help">
            ※emailとtelは少なくとも一方が必須です<br>
            ※1行目はヘッダー行として扱われます
          </p>
          <a @click="downloadTemplate" class="has-text-link">
            <span class="mdi mdi-download"></span>
            テンプレートをダウンロード
          </a>
        </div>
        
        <!-- アップロード進捗 -->
        <div v-if="isUploading" class="upload-progress">
          <progress class="progress is-primary" :value="uploadProgress" max="100">
            {{ uploadProgress }}%
          </progress>
          <p>アップロード中... {{ uploadProgress }}%</p>
        </div>
        
        <!-- 結果表示 -->
        <div v-if="importResult" class="import-result">
          <div class="notification" :class="resultNotificationClass">
            <p><strong>インポート結果</strong></p>
            <p>全体: {{ importResult.total_count }}件</p>
            <p>成功: {{ importResult.success_count }}件</p>
            <p>エラー: {{ importResult.error_count }}件</p>
          </div>
          
          <!-- エラー詳細 -->
          <div v-if="importResult.errors && importResult.errors.length > 0" 
               class="error-details">
            <h4>エラー詳細</h4>
            <div class="error-list">
              <div v-for="(error, index) in importResult.errors" 
                   :key="index" 
                   class="error-item">
                <span class="tag is-danger">行 {{ error.row }}</span>
                {{ error.message }}
                <code v-if="error.data">{{ JSON.stringify(error.data) }}</code>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <footer class="modal-card-foot">
        <button class="button is-primary" 
                @click="startImport" 
                :disabled="!selectedFile || isUploading"
                :class="{ 'is-loading': isUploading }">
          インポート開始
        </button>
        <button class="button" @click="$emit('close')">キャンセル</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  data() {
    return {
      selectedFile: null,
      isUploading: false,
      uploadProgress: 0,
      importResult: null
    }
  },
  computed: {
    resultNotificationClass() {
      if (!this.importResult) return ''
      if (this.importResult.error_count === 0) return 'is-success'
      if (this.importResult.success_count === 0) return 'is-danger'
      return 'is-warning'
    }
  },
  mounted() {
    // アップロード進捗イベントリスナー
    window.addEventListener('upload-progress', this.handleUploadProgress)
  },
  beforeDestroy() {
    window.removeEventListener('upload-progress', this.handleUploadProgress)
  },
  methods: {
    ...mapActions('directtalk', ['importApplicantBlacklist']),
    
    handleDrop(e) {
      e.preventDefault()
      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.setFile(files[0])
      }
    },
    
    handleFileSelect(e) {
      const files = e.target.files
      if (files.length > 0) {
        this.setFile(files[0])
      }
    },
    
    setFile(file) {
      // ファイル形式チェック
      if (!file.name.endsWith('.csv')) {
        alert('CSVファイルを選択してください')
        return
      }
      
      // ファイルサイズチェック（10MB）
      if (file.size > 10 * 1024 * 1024) {
        alert('ファイルサイズは10MB以下にしてください')
        return
      }
      
      this.selectedFile = file
      this.importResult = null
    },
    
    clearFile() {
      this.selectedFile = null
      this.importResult = null
      this.$refs.fileInput.value = ''
    },
    
    downloadTemplate() {
      const csvContent = 'name,email,tel,remarks\n'
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'applicant_blacklist_template.csv'
      link.click()
    },
    
    handleUploadProgress(e) {
      this.uploadProgress = e.detail
    },
    
    async startImport() {
      if (!this.selectedFile) return
      
      this.isUploading = true
      this.uploadProgress = 0
      this.importResult = null
      
      try {
        const response = await this.importApplicantBlacklist(this.selectedFile)
        this.importResult = response.data
        
        if (this.importResult.success_count > 0) {
          this.$emit('imported', this.importResult)
        }
      } catch (error) {
        console.error('Import error:', error)
        const errorMessage = error.response && error.response.data && error.response.data.error 
          ? error.response.data.error 
          : 'インポート処理中にエラーが発生しました'
        alert(errorMessage)
      } finally {
        this.isUploading = false
        this.uploadProgress = 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  width: 600px;
}

.file-upload-area {
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 40px;
  text-align: center;
  margin-bottom: 20px;
  background-color: #f8f8f8;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  .upload-prompt {
    .mdi {
      color: #888;
    }
    
    p {
      margin: 10px 0;
      color: #666;
    }
  }
  
  .selected-file {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    
    .mdi {
      color: #666;
    }
  }
}

.format-info {
  background-color: #f8f8f8;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  
  h4 {
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  table {
    margin-bottom: 10px;
  }
  
  .help {
    font-size: 0.9em;
    margin-bottom: 10px;
  }
  
  a {
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.upload-progress {
  margin: 20px 0;
  
  p {
    margin-top: 10px;
    text-align: center;
  }
}

.import-result {
  margin: 20px 0;
  
  .notification {
    margin-bottom: 20px;
  }
}

.error-details {
  h4 {
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  .error-list {
    max-height: 200px;
    overflow-y: auto;
    
    .error-item {
      padding: 10px;
      border-bottom: 1px solid #eee;
      font-size: 0.9em;
      
      .tag {
        margin-right: 10px;
      }
      
      code {
        display: block;
        margin-top: 5px;
        font-size: 0.85em;
        color: #666;
      }
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/molecules/modals/ImportBlacklistModal.vue